import { Component, OnDestroy, OnInit } from "@angular/core";
import { AbstractControl, UntypedFormBuilder, UntypedFormControl, ValidationErrors, ValidatorFn } from "@angular/forms";
import { MatDialog } from "@angular/material/dialog";
import { Tools } from "@comunicare/comunicare-helper";
import { IReference, IVitalProfileDefinition, Reference } from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import { from, Subject } from "rxjs";
import { concatAll, finalize, first, takeUntil } from "rxjs/operators";
import { Quantity } from "src/app/models/quantity.model";
import { PatientService } from "src/app/providers/patient.service";
import { PatientRegistrationService } from "src/app/providers/patientRegistration.service";
import { SessionService } from "src/app/providers/session.service";
import { VitalProfileDefinitionsService } from "src/app/providers/vital-profile-definition.service";
import { VitalSignService } from "src/app/providers/vital-sign.service";
import { ValueArrayDialogComponent } from "../value-array-dialog/value-array-dialog.component";

@Component({
  selector: "app-vital-signs-form",
  templateUrl: "./vital-signs-form.component.html",
  styleUrls: ["./vital-signs-form.component.scss"],
})
export class VitalSignsFormComponent implements OnInit, OnDestroy {
  private onDestroy$ = new Subject<void>();
  public isLoading = true;
  public vitalSigns: Quantity[] = [];
  public vitalSignform = this.fb.group({});
  public vitalSignControls: { [code: string]: AbstractControl } = {};
  public vitalProfilesDef: IVitalProfileDefinition[];

  constructor(
    private fb: UntypedFormBuilder,
    public patientRegistrationService: PatientRegistrationService,
    private vitalProfileService: VitalProfileDefinitionsService,
    private vitalSignService: VitalSignService,
    public sessionService: SessionService,
    private patientService: PatientService,
    private dialog: MatDialog,
    private translateService: TranslateService
  ) {}

  ngOnInit(): void {
    this.patientService.getPatientUser(this.patientRegistrationService.patientUser.user.caremateIdentifier).subscribe((patientUser) => {
      this.patientRegistrationService.patientUser = patientUser; // to be sure it is up to date
      this.vitalProfileService
        .list(
          this.patientRegistrationService.patientUser.user.caremateIdentifier,
          this.patientRegistrationService.careplan.support[0].reference
        )
        .pipe(first())
        .subscribe((value) => {
          this.vitalProfilesDef = value;
          if (Tools.isDefined(value) && value?.length > 0) {
            this.initForm(value);
            this.isLoading = false;
          } else {
            if (this.patientRegistrationService.isLastStep) {
              this.patientRegistrationService.onFinish();
            } else {
              this.patientRegistrationService.onNextStep$.next();
            }
          }
        });
    });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  private initForm(defList: IVitalProfileDefinition[]): void {
    this.vitalSigns = defList.map((def) => {
      const valueFound = this.patientRegistrationService.patientUser.user.vitalProfile?.find((vp) => def.code === vp.code);

      return new Quantity(
        {
          code: def.code,
          value: Tools.isDefined(valueFound?.value) ? valueFound?.value : null,
          valueArray: valueFound?.valueArray,
          unit: def?.unit ? def.unit[this.sessionService.userLang] : undefined,
        },
        def,
        this.translateService
      );
    });

    // if vitals sign is not set by patient we create empty quantity (only saved if we change it)
    defList.forEach((vpd) => {
      const isPresent = this.vitalSigns.find((vs) => vs.code === vpd.code);
      if (!isPresent) {
        this.vitalSigns.push(
          new Quantity(
            {
              code: vpd.code,
              value: 0,
              valueArray: undefined,
              unit: undefined,
              system: undefined,
            },
            undefined,
            this.translateService
          )
        );
      }
    });

    const group: { [code: string]: UntypedFormControl } = {};
    this.vitalSigns?.forEach((vs) => {
      const def = defList.find((d) => d.code === vs.code);
      if (def?.type !== "array") group[vs.code] = new UntypedFormControl(vs.value, this.valueValidator(vs.code));
    });

    this.vitalSignform = this.fb.group(group);
    this.vitalSignControls = this.vitalSignform.controls;
    // values retrieved from another service may not respect the current validators:
    // mark them as touched so that the errors are displayed immediately
    Object.values(this.vitalSignControls)
      .filter((control) => control.invalid)
      .forEach((control) => control.markAsTouched());
  }

  public valueValidator(controlName: string): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!Tools.isDefined(control.value) || control.value === "") {
        return null;
      }
      const rawValue = control.value.toString().replace(",", ".");
      const v: number = rawValue !== null ? +rawValue : null;
      const currentDefinition = this.getCurrentDefinition(controlName);

      if (!Number.isFinite(v)) {
        return { valueValidator: true };
      }

      // min can be equal to 0, so we must use Tools.isDefined (defaults to 0 when not defined)
      const min = Tools.isDefined(currentDefinition?.min) ? currentDefinition.min : 0;
      if (v < min) {
        return { valueValidator: true, minValue: true };
      }

      // max can be equal to 0, so we must use Tools.isDefined
      if (Tools.isDefined(currentDefinition?.max) && v > currentDefinition.max) {
        return { valueValidator: true, maxValue: true };
      }

      return null;
    };
  }

  public getCurrentDefinition(code: string): IVitalProfileDefinition {
    return this.vitalProfilesDef.find((vpd) => code === vpd.code);
  }

  public save(): void {
    const formValues = this.vitalSignform.getRawValue();
    const savePromises = [];
    const practitionerReference: IReference = {
      display: this.sessionService.account.displayName,
      reference: this.sessionService.account.caremateIdentifier,
    };
    this.vitalProfilesDef.forEach((vp) => {
      if (Tools.isDefined(formValues[vp.code]) && formValues[vp.code] !== "") {
        const previousValue = this.vitalSigns.find((vs) => vs.code === vp.code);
        if (previousValue.value !== formValues[vp.code]) {
          const quantity = new Quantity({
            code: vp.code,
            value: formValues[vp.code],
            unit: vp.unit[this.sessionService.userLang],
          });
          quantity.castValue2NbrAndChangeIt(formValues[vp.code]);
          savePromises.push(
            this.vitalSignService.save(quantity, this.patientRegistrationService.patientUser.user.caremateIdentifier, practitionerReference)
          );
        }
      }
    });
    if (savePromises.length) {
      this.isLoading = true;
      from(savePromises)
        .pipe(
          concatAll(),
          takeUntil(this.onDestroy$),
          finalize(() => {
            // redirection in finalize to avoid the component to be destroyed before the requests
            this.isLoading = false;
            if (this.patientRegistrationService.isLastStep) {
              this.patientRegistrationService.onFinish();
            } else {
              this.patientRegistrationService.onNextStep$.next();
            }
          })
        )
        .subscribe();
    } else {
      if (this.patientRegistrationService.isLastStep) {
        this.patientRegistrationService.onFinish();
      } else {
        this.patientRegistrationService.onNextStep$.next();
      }
    }
  }
  public openValueArrayDialog(vpd: IVitalProfileDefinition): void {
    const vitalSign = this.vitalSigns.find((vs) => vs.code === vpd.code);
    const dialog = this.dialog.open(ValueArrayDialogComponent, {
      data: { vitalSign },
      disableClose: false,
      width: "100%",
    });

    dialog.afterClosed().subscribe((data: { vitalSign: Quantity; mustUpdate: boolean }) => {
      if (data?.vitalSign && data?.mustUpdate) {
        const practitionerReference: Reference = {
          display: this.sessionService.account.displayName,
          reference: this.sessionService.account.caremateIdentifier,
        };
        this.vitalSignService
          .save(data?.vitalSign, this.patientRegistrationService.patientUser.user.caremateIdentifier, practitionerReference)
          .pipe(takeUntil(this.onDestroy$))
          .subscribe();
      }
    });
  }
}
